import { DASH } from "@/constants/testIds";

export default function CompareWidget({ compare }) {
  if (!compare) return null;

  if (!compare.enabled) {
    return (
      <div data-testid={DASH.compareWidget} className="flat-card p-6 sm:p-8">
        <div className="overline mb-2">Behavioural · How you compare</div>
        <h3 className="font-display text-xl tracking-tight">You vs. the Ledger.</h3>
        <p className="text-sm text-[#6E716C] mt-2 max-w-md">{compare.reason}</p>
      </div>
    );
  }

  return (
    <div data-testid={DASH.compareWidget} className="flat-card p-6 sm:p-8">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6">
        <div>
          <div className="overline mb-2">Behavioural · How you compare</div>
          <h3 className="font-display text-2xl tracking-tight">You vs. the Ledger.</h3>
          <p className="text-sm text-[#6E716C] mt-1 max-w-md">
            Anonymised comparison against every Kilowatt Ledger user with real bills. Lower is better on all four.
          </p>
        </div>
        <span className="font-mono-data text-xs text-[#6E716C] whitespace-nowrap">
          n = {compare.sample_size} users
        </span>
      </div>

      <div className="space-y-6">
        {compare.metrics.map((m) => (
          <CompareBar key={m.key} m={m} />
        ))}
      </div>
    </div>
  );
}

function fmtNum(v, digits) {
  if (typeof v !== "number") return "—";
  return v.toLocaleString("en-MY", { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

function CompareBar({ m }) {
  const { user_value, distribution: d, better_than_pct, digits = 0 } = m;
  const range = (d.max - d.min) || 1;
  const clamp = (x) => Math.max(0, Math.min(100, x));
  const pct = (v) => clamp(((v - d.min) / range) * 100);
  const userPos = pct(user_value);

  let badgeText;
  let badgeClass;
  if (better_than_pct >= 75) {
    badgeText = `Top ${Math.max(1, Math.round(100 - better_than_pct))}%`;
    badgeClass = "text-[#0F3524] bg-[#8FA38B]/25";
  } else if (better_than_pct >= 50) {
    badgeText = `Better than ${Math.round(better_than_pct)}% of users`;
    badgeClass = "text-[#0F3524] bg-[#F0F0EA]";
  } else if (better_than_pct >= 25) {
    badgeText = "Around the median · room to improve";
    badgeClass = "text-[#B24F35] bg-[#F0F0EA]";
  } else {
    badgeText = `Bottom ${Math.max(1, Math.round(better_than_pct))}% · room to improve`;
    badgeClass = "text-[#B24F35] bg-[#B24F35]/10";
  }

  return (
    <div data-testid={DASH.compareMetric(m.key)}>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <span className="font-mono-data text-sm text-[#6E716C]">{m.label}</span>
          <span className="ml-3 font-mono-data text-xl text-[#0F3524]">
            {fmtNum(user_value, digits)}
            <span className="ml-1 text-xs text-[#6E716C]">{m.unit}</span>
          </span>
        </div>
        <span className={`text-[10px] font-mono-data uppercase tracking-widest px-2 py-1 whitespace-nowrap ${badgeClass}`}>
          {badgeText}
        </span>
      </div>

      <div className="relative h-6 bg-[#F0F0EA]" role="img" aria-label={`Your position: ${better_than_pct}% better than peers`}>
        {/* p25 / p50 / p75 ticks */}
        <div className="absolute top-0 bottom-0 w-px bg-[#C5C5BE]" style={{ left: `${pct(d.p25)}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-[#6E716C]" style={{ left: `${pct(d.p50)}%` }} />
        <div className="absolute top-0 bottom-0 w-px bg-[#C5C5BE]" style={{ left: `${pct(d.p75)}%` }} />
        {/* user marker */}
        <div
          className="absolute -top-1 -bottom-1 w-[3px] bg-[#B24F35]"
          style={{ left: `calc(${userPos}% - 1.5px)` }}
          title="You"
        />
      </div>
      <div className="flex justify-between text-[10px] font-mono-data text-[#6E716C] mt-1">
        <span>min {fmtNum(d.min, digits)}</span>
        <span>median {fmtNum(d.p50, digits)}</span>
        <span>max {fmtNum(d.max, digits)}</span>
      </div>
    </div>
  );
}
